// Safe Google Ads / GA4 conversion tracking helpers.
// No-ops if gtag / dataLayer aren't installed, so it never breaks the page.
import { canSendAdvertising, recordAdvertisingConversion } from './advertisingConsent';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export type AdsEvent =
  | 'phone_call_click'
  | 'sms_click'
  | 'apply_form_submit'
  | 'apply_cta_click'
  | 'course_enquiry_success'
  | 'package_select';

/** Owner-confirmed successful enquiry conversion; no purchase value. */
export function getEnquiryConversionSendTo(
  value: unknown = 'AW-18310421880/O_rmCLXOh6UdEPi6i5tE',
): string | null {
  return typeof value === 'string' && /^AW-18310421880\/[A-Za-z0-9_-]{6,}$/.test(value.trim()) ? value.trim() : null;
}

/**
 * Fire a Google Ads / GA4 conversion event.
 * Set VITE_GADS_CONVERSION_ID + VITE_GADS_PHONE_LABEL in .env for phone-call
 * conversion tracking (format: "AW-XXXXXXXXX/abcDEFghi").
 */
export function trackAdsEvent(event: AdsEvent, params: Record<string, unknown> = {}) {
  try {
    if (typeof window === 'undefined' || !canSendAdvertising()) return;
    // This outcome never accepts caller-supplied personal or monetary parameters.
    const safeParams = event === 'course_enquiry_success' ? {} : params;

    // GA4 event
    window.gtag?.('event', event, safeParams);

    // GTM dataLayer push (in case Tag Manager is used instead of gtag.js)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...safeParams });

    // Google Ads "Outbound click" conversion — fires for Call Ian & Message Ian buttons
    if (event === 'phone_call_click' || event === 'sms_click') {
      window.gtag?.('event', 'conversion', {
        send_to: 'AW-18310421880/0jVBCMqXldAcEPi6i5tE',
      });
    }

    // "Intensive Course Enquiry Submitted" — only called after backend success.
    if (event === 'course_enquiry_success') {
      recordAdvertisingConversion();
      const sendTo = getEnquiryConversionSendTo();
      if (sendTo) window.gtag?.('event', 'conversion', { send_to: sendTo });
    }
  } catch {
    // Never let tracking break user flow
  }
}
